import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, ChevronDown, ChevronUp, MapPin, Phone, Mail, Globe, ClipboardList } from 'lucide-react';

export default function PendingSubcontractorsPanel({ onChanged }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [busy, setBusy] = useState(null); // id when an action is in flight

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await api.get('/subcontractors/pending');
      setPending(data || []);
    } catch {
      // 403 if not admin → silently ignore
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const approve = async (sub) => {
    if (!window.confirm(`Valider le compte de ${sub.company_name} ?`)) return;
    setBusy(sub.id);
    try {
      await api.post(`/subcontractors/${sub.id}/approve`);
      toast.success(`${sub.company_name} validé`);
      await fetchPending();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setBusy(null);
    }
  };

  const reject = async (sub) => {
    if (!window.confirm(`Rejeter et supprimer la demande de ${sub.company_name} ? Cette action est irréversible.`)) return;
    setBusy(sub.id);
    try {
      await api.post(`/subcontractors/${sub.id}/reject`);
      toast.success(`Demande rejetée`);
      await fetchPending();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors du rejet');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return null;
  if (pending.length === 0) return null;

  return (
    <div className="bg-amber-500/5 border border-amber-500/30 rounded-sm" data-testid="pending-subs-panel">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-amber-500/10 transition-colors"
        data-testid="pending-toggle-btn"
      >
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-semibold text-amber-200">
            Sous-traitants à valider
          </span>
          <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-500 text-zinc-950 font-bold" data-testid="pending-count-badge">
            {pending.length}
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-amber-400" /> : <ChevronDown className="w-4 h-4 text-amber-400" />}
      </button>
      {expanded && (
        <div className="border-t border-amber-500/20 divide-y divide-amber-500/10">
          {pending.map(sub => (
            <div key={sub.id} className="px-4 py-3 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3" data-testid={`pending-sub-${sub.id}`}>
              <div className="space-y-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-zinc-100">{sub.company_name}</span>
                  <span className="text-xs text-zinc-400">·</span>
                  <span className="text-xs text-zinc-400">{sub.contact_name}</span>
                  {sub.self_registered && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-500/15 border border-blue-500/30 text-blue-300">Inscription libre</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
                  {sub.email && (<span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {sub.email}</span>)}
                  {sub.phone && (<span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {sub.phone}</span>)}
                  {sub.country && (<span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> {sub.country}</span>)}
                  {sub.address && (<span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {sub.address}</span>)}
                </div>
                {(sub.specialties && sub.specialties.length > 0) ? (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {sub.specialties.map(s => (
                      <span key={s} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-800 border border-zinc-700 text-zinc-300">{s}</span>
                    ))}
                  </div>
                ) : sub.specialty ? (
                  <div className="text-[11px] text-zinc-500 italic">{sub.specialty}</div>
                ) : null}
                <div className="text-[10px] text-zinc-600 mt-1">
                  Inscrit le {new Date(sub.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                  {sub.language && ` · langue ${sub.language.toUpperCase()}`}
                </div>
              </div>
              <div className="flex items-center gap-2 self-center">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy === sub.id}
                  onClick={() => reject(sub)}
                  className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-8"
                  data-testid={`reject-sub-${sub.id}`}
                >
                  {busy === sub.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><XCircle className="w-3.5 h-3.5 mr-1" /> Rejeter</>}
                </Button>
                <Button
                  size="sm"
                  disabled={busy === sub.id}
                  onClick={() => approve(sub)}
                  className="bg-emerald-600 hover:bg-emerald-500 text-zinc-50 h-8"
                  data-testid={`approve-sub-${sub.id}`}
                >
                  {busy === sub.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approuver</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
